/**
 * LogActivityDialog — replica el diálogo "Log Call / Text Message" de Zicasso.
 * Registra en Zicasso (interno, no lo ve el viajero) una llamada contestada /
 * no contestada / SMS enviado, con notas en texto enriquecido, y avanza el
 * recordatorio. Se usa desde: modo Foco de Tareas (con taskId), ficha del lead
 * y tras enviar un WhatsApp (con defaultKind="text").
 */
import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Bold, Italic, Underline, Strikethrough, Link as LinkIcon, List, ListOrdered, Phone, PhoneOff, MessageSquare } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const KINDS = [
  { value: "answered", label: "Llamada: el viajero contestó", Icon: Phone },
  { value: "no_answer", label: "Llamada: no contestó", Icon: PhoneOff },
  { value: "text", label: "SMS / WhatsApp enviado", Icon: MessageSquare },
];

function ToolbarButton({ title, onClick, children, testid }) {
  return (
    <button
      type="button"
      title={title}
      data-testid={testid}
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
    >
      {children}
    </button>
  );
}

export function LogActivityDialog({ open, onOpenChange, lead, taskId, agentId, defaultKind = "answered", initialNotesHtml = "", onLogged }) {
  const qc = useQueryClient();
  const [kind, setKind] = useState(defaultKind);
  const editorRef = useRef(null);

  useEffect(() => {
    if (open) {
      setKind(defaultKind);
      if (editorRef.current) editorRef.current.innerHTML = initialNotesHtml || "";
    }
  }, [open, defaultKind, initialNotesHtml]);

  const exec = (cmd) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, null);
  };
  const addLink = () => {
    const url = window.prompt("URL del enlace:", "https://");
    if (url) { editorRef.current?.focus(); document.execCommand("createLink", false, url); }
  };

  const getNotesHtml = () => {
    const html = (editorRef.current?.innerHTML || "").trim();
    const text = (editorRef.current?.innerText || "").trim();
    return text ? html : "";
  };

  const mut = useMutation({
    mutationFn: () => {
      const notes_html = getNotesHtml();
      if (taskId) {
        const q = agentId && agentId !== "__all__" ? `?agent_id=${agentId}` : "";
        return api.post(`/zicasso/tasks/${taskId}/complete${q}`, {
          action: "call", activity_kind: kind, notes_html,
        });
      }
      return api.post(`/zicasso/leads/${lead.id}/log-activity`, { kind, notes_html });
    },
    onSuccess: (r) => {
      toast.success(r.data?.demo ? "Registrado (demo)" : "Registrado en Zicasso");
      qc.invalidateQueries({ queryKey: ["tasks-today"] });
      qc.invalidateQueries({ queryKey: ["tasks-summary"] });
      if (lead?.id) qc.invalidateQueries({ queryKey: ["zicasso-lead", lead.id] });
      onOpenChange(false);
      onLogged?.(r.data);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo registrar"),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="log-activity-dialog">
        <DialogHeader>
          <DialogTitle>Registrar llamada / mensaje</DialogTitle>
        </DialogHeader>

        <div className="space-y-1.5" data-testid="log-activity-options">
          {KINDS.map(({ value, label, Icon }) => (
            <label
              key={value}
              className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 text-sm transition-colors ${
                kind === value ? "border-[#34406B] bg-[#34406B]/5" : "border-border hover:bg-accent"
              }`}
              data-testid={`log-activity-kind-${value}`}
            >
              <input
                type="radio"
                name="log-kind"
                value={value}
                checked={kind === value}
                onChange={() => setKind(value)}
                className="accent-[#34406B]"
              />
              <Icon className="h-4 w-4 text-[#34406B]" />
              <span>{label}</span>
            </label>
          ))}
        </div>

        <div className="rounded-lg border">
          <div className="flex items-center gap-0.5 border-b px-1.5 py-1">
            <ToolbarButton title="Negrita" onClick={() => exec("bold")} testid="log-fmt-bold"><Bold className="h-4 w-4" /></ToolbarButton>
            <ToolbarButton title="Cursiva" onClick={() => exec("italic")} testid="log-fmt-italic"><Italic className="h-4 w-4" /></ToolbarButton>
            <ToolbarButton title="Subrayado" onClick={() => exec("underline")} testid="log-fmt-underline"><Underline className="h-4 w-4" /></ToolbarButton>
            <ToolbarButton title="Tachado" onClick={() => exec("strikeThrough")} testid="log-fmt-strike"><Strikethrough className="h-4 w-4" /></ToolbarButton>
            <ToolbarButton title="Enlace" onClick={addLink} testid="log-fmt-link"><LinkIcon className="h-4 w-4" /></ToolbarButton>
            <ToolbarButton title="Lista" onClick={() => exec("insertUnorderedList")} testid="log-fmt-ul"><List className="h-4 w-4" /></ToolbarButton>
            <ToolbarButton title="Lista numerada" onClick={() => exec("insertOrderedList")} testid="log-fmt-ol"><ListOrdered className="h-4 w-4" /></ToolbarButton>
          </div>
          <div
            ref={editorRef}
            contentEditable
            data-testid="log-activity-notes"
            data-placeholder="Notas de la llamada (opcional)…"
            className="prose-editor min-h-[110px] px-3 py-2 text-sm focus:outline-none"
            suppressContentEditableWarning
          />
        </div>
        <p className="text-[11px] text-muted-foreground">
          Las llamadas y SMS son internos: no los ve el viajero. Se guardan también en Zicasso.
        </p>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mut.isPending} data-testid="log-activity-cancel">
            Cancelar
          </Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending} data-testid="log-activity-submit">
            {mut.isPending ? "Registrando…" : "Registrar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default LogActivityDialog;
